import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useDismissGuard } from './dismissGuard.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import {
  PLACE_MIN_CHARS, QUICK_CLIENTS_SHOWN, useQuickClientSearch, useQuickPlaceSearch, type PlaceMatch,
} from '../api/quickSearch.ts';
import { LoadFailure, isSessionFailure } from './LoadFailure.tsx';
import { HeldMarker, StatusTag } from './StatusTag.tsx';
import { rowLoader } from './revealRegistry.ts';
import type { ClientListRow } from '../api/types.ts';

const DEBOUNCE_MS = 120;
/** How long a page opened by the search is given to draw the row it was opened for. */
const REVEAL_WAIT_MS = 10_000;
const HIGHLIGHT_MS = 2600;

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const FIND = isMac ? '⌘F' : 'Ctrl+F';

/**
 * Quick search: Ctrl+F (⌘F on a Mac) on every signed-in screen opens a search
 * over the data, in place of the browser's find bar.
 *
 * **Why not the browser's find.** It searches what is drawn. A list that is
 * filtered, paged, or one day virtualised holds rows the find bar cannot see, and
 * "no matches" there reads as "this client does not exist". This searches the
 * server: clients by name, reference, alias and former name (`GET /clients`),
 * and places by name where the caller's clients are now (`GET /places`).
 *
 * **What choosing a result does.** A BUILDING opens its facility page, always
 * (8 October: search arrives somewhere; the board's modal is for clicking a
 * building in context). Otherwise, if the page on screen has that client's row,
 * or that place's card or rows, it scrolls there, highlights them and moves focus
 * to the first. Otherwise it navigates: a client opens their record; any other
 * place opens its card on the facility board for someone who holds it, and
 * otherwise lists the clients there. There are no match counts and no Enter-to-cycle: with
 * names that rarely overlap, the job is jumping to the one intended row.
 *
 * **How a page takes part.** It marks what the search may reveal:
 *
 * - `data-quick-client`: the client id, or several separated by spaces, on the
 *   row or card for them.
 * - `data-quick-location`: a facility board location key; and
 *   `data-quick-location-also`, more keys separated by spaces, on a tile that
 *   holds several places (the homes at one address, 13 October).
 *
 * The first focusable element inside, or the element itself, takes focus. A
 * page that marks nothing still gets the search, and its results navigate.
 *
 * **Escape closes it.** Pressing Ctrl+F again while it is open closes it and lets
 * the browser's own find bar open, for text on the page that is not a client or
 * a place.
 */
/**
 * `enabled` is false for a reader with no client pages (the receptionist since
 * 113): there is nothing for it to find, and Ctrl+F stays the browser's own.
 */
export function QuickSearchProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const openRef = useRef(false);
  openRef.current = open;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const returnFocus = useRef<HTMLElement | null>(null);
  const pendingLabel = useRef('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!enabledRef.current) return;
      if (e.key.toLowerCase() !== 'f' || e.altKey || e.shiftKey || e.isComposing) return;
      if (!(isMac ? e.metaKey : e.ctrlKey)) return;
      // A second press while open is for the browser's own find: close, and let it through.
      if (openRef.current) {
        setOpen(false);
        return;
      }
      e.preventDefault();
      const selected = window.getSelection()?.toString().trim() ?? '';
      setInitial(selected.length <= 60 && !selected.includes('\n') ? selected : '');
      returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, []);

  // A page opened by the search, for a place's card: wait for it to be drawn.
  // The card to reveal travels in the history entry's state, never the URL: a
  // place key can be a stay's free-text location (16 October, url-guard.ts).
  useEffect(() => {
    const state = (location.state ?? null) as { reveal?: string } | null;
    const wanted = state?.reveal;
    if (!wanted) return;
    const split = wanted.indexOf(':');
    const target: Target = { kind: wanted.slice(0, split) === 'client' ? 'client' : 'location', id: wanted.slice(split + 1) };
    const tidy = () => navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: { ...state, reveal: undefined } },
    );

    let finished = false;
    const attempt = () => {
      if (finished) return;
      const found = targetsFor(target);
      if (found.length === 0) return;
      finished = true;
      observer.disconnect();
      window.clearTimeout(timer);
      tidy();
      window.setTimeout(() => reveal(targetsFor(target)), 0);
    };
    const observer = new MutationObserver(attempt);
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = window.setTimeout(() => {
      if (finished) return;
      finished = true;
      observer.disconnect();
      tidy();
      setNotice(`${pendingLabel.current || 'That'} is not on this page.`);
    }, REVEAL_WAIT_MS);
    attempt();
    return () => {
      finished = true;
      observer.disconnect();
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navigate]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const close = () => {
    setOpen(false);
    const back = returnFocus.current;
    returnFocus.current = null;
    if (back?.isConnected) window.setTimeout(() => back.focus({ preventScroll: true }), 0);
  };

  /**
   * Reveal a row once it has been drawn, rather than once it has been fetched.
   *
   * `loadUntil` resolves when the rows are in the cache. React has not committed
   * them to the page at that moment — on the filed reports that is 2,719 rows to
   * render — so revealing on a bare timeout looked for an element that did not
   * exist yet, found nothing, and returned silently. The row loaded and the page
   * neither scrolled to it nor highlighted it, which reads as the search having
   * done nothing at all. This waits for the element, exactly as the revealed card
   * path above does, and says so if it never arrives.
   */
  const revealWhenDrawn = (target: Target, label: string) => {
    let done = false;
    const attempt = () => {
      if (done) return;
      const found = targetsFor(target);
      if (found.length === 0) return;
      done = true;
      observer.disconnect();
      window.clearTimeout(timer);
      reveal(found);
    };
    const observer = new MutationObserver(attempt);
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = window.setTimeout(() => {
      if (done) return;
      done = true;
      observer.disconnect();
      setNotice(`${label} is not on this page.`);
    }, REVEAL_WAIT_MS);
    attempt();
  };

  const choose = (choice: Choice) => {
    setOpen(false);
    returnFocus.current = null;
    // After the dialog has left the top layer, which would otherwise keep focus.
    if (choice.target && targetsFor(choice.target).length > 0) {
      const target = choice.target;
      window.setTimeout(() => reveal(targetsFor(target)), 0);
      return;
    }
    // Not drawn yet, but this page may be a list that can fetch it. A match in a
    // scrolling list is often simply below what has loaded; taking the reader out
    // of the list to the client's record would lose their place, which is the
    // friction the scrolling list exists to remove.
    const loader = choice.target?.kind === 'client' ? rowLoader() : null;
    if (loader && choice.target) {
      const target = choice.target;
      setNotice(`Looking for ${choice.label} further down the list…`);
      void loader(target.id).then((reached) => {
        setNotice(null);
        if (reached) {
          revealWhenDrawn(target, choice.label);
        } else if (choice.navigateTo) {
          pendingLabel.current = choice.label;
          navigate(choice.navigateTo, { state: choice.reveal ? { reveal: choice.reveal } : {} });
        }
      });
      return;
    }
    if (choice.navigateTo) {
      pendingLabel.current = choice.label;
      navigate(choice.navigateTo, choice.reveal ? { state: { reveal: choice.reveal } } : undefined);
    }
  };

  return (
    <>
      {children}
      {open && <QuickSearchDialog initial={initial} onClose={close} onChoose={choose} />}
      {notice && (
        <p
          role="status"
          className="fixed top-4 left-1/2 z-50 -translate-x-1/2 rounded-full bg-neutral-900 px-4 py-2 text-sm
                     font-semibold text-white shadow-lg"
        >
          {notice}
        </p>
      )}
    </>
  );
}

type Target = { kind: 'client' | 'location'; id: string };
/** `reveal`: the card to show on the page opened, carried in history state, never in the URL. */
type Choice = { label: string; target?: Target; navigateTo?: string; reveal?: string };

function targetsFor(target: Target): HTMLElement[] {
  const selector = target.kind === 'client'
    ? `[data-quick-client~="${CSS.escape(target.id)}"]`
    : `[data-quick-location="${CSS.escape(target.id)}"], [data-quick-location-also~="${CSS.escape(target.id)}"]`;
  return [...document.querySelectorAll<HTMLElement>(selector)];
}

/** Scroll to the first, highlight all, and put focus on the first. */
function reveal(elements: HTMLElement[]) {
  const [first] = elements;
  if (!first) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  first.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  for (const el of elements) {
    el.classList.remove('quick-found');
    void el.offsetWidth; // restarts the highlight if it is already running
    el.classList.add('quick-found');
    window.setTimeout(() => el.classList.remove('quick-found'), HIGHLIGHT_MS);
  }
  const focusable = first.matches('a[href], button') ? first : first.querySelector<HTMLElement>('a[href], button');
  focusable?.focus({ preventScroll: true });
}

/** What the page on screen can show in place, read once as the dialog opens. */
function readPage() {
  const clients = new Set<string>();
  for (const el of document.querySelectorAll<HTMLElement>('[data-quick-client]')) {
    for (const id of (el.dataset.quickClient ?? '').split(' ')) if (id) clients.add(id);
  }
  const locations = new Set<string>();
  for (const el of document.querySelectorAll<HTMLElement>('[data-quick-location]')) {
    if (el.dataset.quickLocation) locations.add(el.dataset.quickLocation);
    for (const key of (el.dataset.quickLocationAlso ?? '').split(' ')) if (key) locations.add(key);
  }
  return { clients, locations };
}

type Option =
  | { kind: 'client'; key: string; row: ClientListRow | PlaceMatch['clients'][number]; here: boolean }
  | { kind: 'place'; key: string; place: PlaceMatch; here: boolean };

function QuickSearchDialog({
  initial, onClose, onChoose,
}: {
  initial: string;
  onClose: () => void;
  onChoose: (choice: Choice) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const id = useId();
  const [typed, setTyped] = useState(initial);
  const [q, setQ] = useState(initial.trim());
  const [active, setActive] = useState(0);
  const [drilled, setDrilled] = useState<PlaceMatch | null>(null);
  const [page] = useState(readPage);
  const mine = useMyDashboards();
  const canOpenBoard = mine.data?.dashboards.includes('director') ?? false;

  const closed = useRef(onClose);
  closed.current = onClose;
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const handle = () => closed.current();
    dialog.addEventListener('close', handle); // a fallback; every way out below closes directly
    if (!dialog.open) dialog.showModal();
    input.current?.select();
    return () => dialog.removeEventListener('close', handle);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(typed.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);
  useEffect(() => setActive(0), [q, drilled]);

  const clients = useQuickClientSearch(drilled ? '' : q);
  const places = useQuickPlaceSearch(drilled ? '' : q);
  const text = typed.trim();
  const wantsPlaces = q.length >= PLACE_MIN_CHARS;

  const clientFailure = clients.error ?? (clients.isPlaceholderData ? clients.failureReason : null);
  const placeFailure = wantsPlaces ? places.error ?? (places.isPlaceholderData ? places.failureReason : null) : null;
  const failure = clientFailure ?? placeFailure;
  const sessionFailed = failure !== null && isSessionFailure(failure);
  const clientsReady = q !== '' && clients.isSuccess && !clients.isPlaceholderData;
  const placesReady = !wantsPlaces || (places.isSuccess && !places.isPlaceholderData) || placeFailure !== null;
  // An answer to what is in the box now, not to what was in it a moment ago.
  const answered = !drilled && q !== '' && q === text && clientsReady && placesReady;
  const searching = !drilled && text !== '' && !answered && !sessionFailed;

  // The page's own clients first: the one on screen is usually the one wanted.
  const matched = clientFailure || q === '' ? [] : clients.data?.clients ?? [];
  const ranked = [
    ...matched.filter((row) => page.clients.has(row.client_id)),
    ...matched.filter((row) => !page.clients.has(row.client_id)),
  ].slice(0, QUICK_CLIENTS_SHOWN);
  const moreClients = (clients.data?.total ?? 0) > ranked.length && !drilled && answered;

  const options: Option[] = drilled
    ? drilled.clients.map((row) => ({ kind: 'client', key: row.client_id, row, here: page.clients.has(row.client_id) }))
    : [
        ...ranked.map((row): Option => ({
          kind: 'client', key: row.client_id, row, here: page.clients.has(row.client_id),
        })),
        ...(placeFailure || !wantsPlaces ? [] : places.data?.places ?? []).map((place): Option => ({
          kind: 'place', key: place.location_key, place, here: page.locations.has(place.location_key),
        })),
      ];
  const usable = drilled !== null || answered;

  const dismiss = () => {
    if (ref.current?.open) ref.current.close();
    closed.current();
  };

  const pick = (option: Option) => {
    if (option.kind === 'client') {
      const clientId = option.row.client_id;
      // The target is given whether or not the row is drawn on the page now.
      // `choose` reveals it if it is, and otherwise offers it to the row loader
      // the page registered, which loads up to it rather than leaving the list.
      // Withholding the target unless it was already drawn made that loader
      // unreachable in the one case it exists for — a row further down a list
      // that has not loaded that far yet — so the search always navigated away.
      // `navigateTo` stays: it is the fallback when the loader cannot find it,
      // which is a client the list's own filters exclude.
      onChoose({
        label: option.row.display_name,
        target: { kind: 'client', id: clientId },
        navigateTo: `/clients/${clientId}`,
      });
      return;
    }
    const { place } = option;
    // SEARCHING FOR A BUILDING ARRIVES AT ITS PAGE (8 October). Clicking a
    // building in context (a board card, a placement) still opens the modal,
    // a glance without leaving the work; search is an act of arriving
    // somewhere, so it lands on the full page, wherever it is pressed. A place
    // that is not a building (a home, a household, a hospital stay elsewhere)
    // has no page and keeps what it did.
    const facilityId = place.location_key.startsWith('facility:') ? place.location_key.slice('facility:'.length) : null;
    if (facilityId) {
      onChoose({ label: place.location, navigateTo: `/facilities/${facilityId}` });
    } else if (option.here) {
      onChoose({ label: place.location, target: { kind: 'location', id: place.location_key } });
    } else if (canOpenBoard) {
      onChoose({ label: place.location, navigateTo: '/visits/board?view=locations', reveal: `location:${place.location_key}` });
    } else {
      setDrilled(place);
      input.current?.focus();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (drilled) setDrilled(null);
      else dismiss();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (usable && options[active]) pick(options[active]);
      else if (text !== q) setQ(text); // typed faster than the debounce: search now
    } else if (e.key === 'Backspace' && drilled && typed === '') {
      setDrilled(null);
    }
  };

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // A-#195: the backdrop closes only when pressed and released on it; nothing typed here is an edit to lose
  const backdrop = useDismissGuard(dismiss, { changes: false });
  const optionId = (i: number) => `${id}-option-${i}`;
  const headerFor = (i: number): string | null => {
    const option = options[i]!;
    const before = options[i - 1];
    if (drilled) {
      return i === 0 ? `${drilled.client_count} ${drilled.client_count === 1 ? 'client' : 'clients'} here` : null;
    }
    if (option.kind === 'place') return before?.kind === 'place' ? null : 'Places';
    if (!before) return option.here ? 'On this page' : 'Clients';
    return before.kind === 'client' && before.here && !option.here ? 'Other clients' : null;
  };

  return (
    <dialog
      ref={ref}
      aria-label="Quick search"
      onKeyDown={onKeyDown}
      onPointerDown={backdrop.dialogProps.onPointerDown}
      onClick={backdrop.dialogProps.onClick}
      className="mx-auto mt-[8vh] mb-auto w-[min(42rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl bg-white p-0
                 text-neutral-900 shadow-2xl ring-1 ring-black/10 backdrop:bg-neutral-950/40
                 backdrop:backdrop-blur-[2px] sm:mt-[12vh]"
    >
      <div className="flex items-center gap-3 border-b border-neutral-200 px-4">
        <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 shrink-0 fill-none stroke-neutral-400 stroke-2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
        {drilled && (
          <button
            type="button"
            onClick={() => { setDrilled(null); input.current?.focus(); }}
            className="inline-flex min-h-[36px] shrink-0 items-center gap-1 rounded-lg bg-neutral-100 px-2 text-sm
                       font-semibold text-neutral-700 hover:bg-neutral-200"
          >
            <span aria-hidden="true">←</span> <span className="max-w-[12rem] truncate">{drilled.location}</span>
          </button>
        )}
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={options.length > 0}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={usable && options[active] ? optionId(active) : undefined}
          aria-label="Find a client by name, reference or former name, or a place by name"
          autoComplete="off"
          spellCheck={false}
          value={drilled ? '' : typed}
          readOnly={drilled !== null}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={drilled ? `Who is at ${drilled.location}` : 'Jump to a client or a place'}
          className="h-14 min-w-0 flex-1 bg-transparent text-lg text-neutral-900 outline-none
                     placeholder:text-neutral-500 [&::-webkit-search-cancel-button]:hidden"
        />
        {searching && (
          <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4 shrink-0 motion-safe:animate-spin">
            <circle cx="12" cy="12" r="9" className="fill-none stroke-neutral-200 stroke-[3]" />
            <path d="M21 12a9 9 0 0 0-9-9" className="fill-none stroke-neutral-500 stroke-[3]" strokeLinecap="round" />
          </svg>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg
                     hover:bg-neutral-100"
          aria-label="Close quick search"
        >
          <Kbd>Esc</Kbd>
        </button>
      </div>

      <div className="max-h-[min(28rem,60vh)] overflow-y-auto">
        {failure && (sessionFailed || (clientFailure && !clients.data)) ? (
          <LoadFailure
            what="The search"
            verb="could not be run"
            error={failure}
            onRetry={() => { void clients.refetch(); if (wantsPlaces) void places.refetch(); }}
            retrying={clients.isFetching || places.isFetching}
          />
        ) : !drilled && text === '' ? (
          <div className="px-6 py-8 text-center">
            <p className="font-semibold text-neutral-800">Jump straight to anyone</p>
            <p className="mx-auto mt-1 max-w-[30rem] text-sm text-neutral-500">
              A name, a former name or a reference finds a client; the name of a building or a street finds a
              place. It searches everyone you can see, not only what is on this page.
            </p>
          </div>
        ) : answered && options.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <p className="font-semibold text-neutral-800">Nobody and nowhere matches “{q}”</p>
            <p className="mt-1 text-sm text-neutral-500">Former names and aliases are searched too.</p>
          </div>
        ) : (
          <ul
            ref={listRef}
            id={`${id}-list`}
            role="listbox"
            aria-label={drilled ? `Clients at ${drilled.location}` : 'Matching clients and places'}
            // The previous text's matches stay while the next search runs, at full contrast: words say
            // they are out of date, never a fade (a fade took their grey lines to 2.3 : 1, 15 October).
            aria-busy={!usable}
            className="p-2"
          >
            {!usable && (
              <li role="presentation" className="px-3 pt-1 pb-2 text-sm text-neutral-600">Searching…</li>
            )}
            {options.map((option, i) => {
              return (
                <Result
                  key={`${option.kind}:${option.key}`}
                  header={headerFor(i)}
                  id={optionId(i)}
                  index={i}
                  option={option}
                  q={drilled ? '' : q}
                  active={usable && i === active}
                  canOpenBoard={canOpenBoard}
                  onHover={() => setActive(i)}
                  onPick={() => usable && pick(option)}
                />
              );
            })}
            {moreClients && (
              <li role="presentation" className="px-3 pt-1 pb-2 text-sm text-neutral-500">
                {(clients.data?.total ?? 0).toLocaleString()} clients match. Keep typing to narrow it.
              </li>
            )}
            {drilled && drilled.client_count > drilled.clients.length && (
              <li role="presentation" className="px-3 py-2 text-sm text-neutral-500">
                And {drilled.client_count - drilled.clients.length} more. Search for a name to find them.
              </li>
            )}
          </ul>
        )}
        {placeFailure && !sessionFailed && clients.data && (
          <p className="border-t border-neutral-200 px-4 py-2 text-sm text-neutral-600">
            Places could not be searched just now; clients were.
          </p>
        )}
      </div>

      <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-neutral-200 bg-neutral-50 px-4 py-2
                         text-xs text-neutral-500">
        <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> to move</span>
        <span className="flex items-center gap-1"><Kbd>↵</Kbd> to go</span>
        <span className="flex items-center gap-1"><Kbd>Esc</Kbd> to close</span>
        <span className="ml-auto hidden sm:inline">{FIND} again for the browser’s find</span>
      </footer>
    </dialog>
  );
}

function Result({
  header, id, index, option, q, active, canOpenBoard, onHover, onPick,
}: {
  header: string | null;
  id: string;
  index: number;
  option: Option;
  q: string;
  active: boolean;
  canOpenBoard: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const muted = active ? 'text-white/70' : 'text-neutral-500';
  let badge: ReactNode;
  let title: string;
  let detail: ReactNode;
  let action: string;
  let trailing: ReactNode = null;

  if (option.kind === 'client') {
    const row = option.row;
    title = row.display_name;
    const full = 'where_to_find_them' in row ? row : null;
    // Word by word, as the server matches (search.ts): the hit came from an alias
    // only if some word appears in neither the shown name nor the ref. As one
    // substring, "Oswin Okafor" would find "Okafor, Oswin Jr." and then be
    // labelled an alias match, which it is not. One word: exactly as before.
    const shown = `${row.display_name} ${row.client_ref}`.toLowerCase();
    const words = q.toLowerCase().split(/\s+/).filter((w) => w.length > 0);
    const formerName = words.length > 0 && words.some((w) => !shown.includes(w));
    const where = full ? (full.whereabouts_unknown ? 'Whereabouts unknown' : full.where_to_find_them ?? 'Not recorded') : null;
    detail = [formerName ? 'Matched a former name, alias or past address' : null, where].filter(Boolean).join(' · ') || row.client_ref;
    badge = <Initials name={row.display_name} active={active} />;
    action = option.here ? 'Show in this list' : 'Open';
    trailing = (
      <>
        {/* Two clients can share a name; a status picks the right one (8 October). */}
        {full && <StatusTag status={full.legal_status} className={active ? 'border-white/50 text-white' : ''} />}
        {full && <HeldMarker held={full.held_at_load} className={active ? 'border-white/60 text-white' : ''} />}
        {full && full.critical_flags > 0 && (
          <span className="shrink-0 rounded bg-critical-bg px-2 py-0.5 text-xs font-bold tracking-wide text-critical-fg uppercase">
            Critical
          </span>
        )}
        <span className={`shrink-0 font-mono text-xs tabular-nums ${muted}`}>{row.client_ref}</span>
      </>
    );
  } else {
    const { place } = option;
    title = place.location;
    detail = [
      place.location_address,
      `${place.client_count} ${place.client_count === 1 ? 'client' : 'clients'} here`,
    ].filter(Boolean).join(' · ');
    badge = <PlaceIcon active={active} />;
    action = place.location_key.startsWith('facility:') ? 'Open the facility'
      : option.here ? 'Show on this page' : canOpenBoard ? 'Show on the board' : 'Who is here';
  }

  return (
    <>
      {header && (
        <li role="presentation" className="px-3 pt-2 pb-1 text-[0.6875rem] font-bold tracking-[0.14em] text-neutral-600 uppercase">
          {header}
        </li>
      )}
      <li
        id={id}
        role="option"
        aria-selected={active}
        data-index={index}
        onMouseDown={(e) => e.preventDefault()}
        onMouseMove={onHover}
        onClick={onPick}
        className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl px-3 py-2
                    ${active ? 'bg-neutral-900 text-white' : 'text-neutral-900'}`}
      >
        {badge}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold"><Match text={title} q={q} /></span>
          <span className={`block truncate text-sm ${muted}`}>{detail}</span>
        </span>
        {trailing}
        <span
          className={`hidden shrink-0 items-center gap-1.5 text-xs font-semibold sm:flex ${active ? 'text-white' : 'invisible'}`}
          aria-hidden="true"
        >
          {action} <Kbd dark={active}>↵</Kbd>
        </span>
      </li>
    </>
  );
}

/** The searched text, underlined where it matched. Not coloured: colour is kept for Critical. */
function Match({ text, q }: { text: string; q: string }) {
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-transparent font-extrabold text-inherit underline decoration-2 underline-offset-[3px]">
        {text.slice(at, at + q.length)}
      </mark>
      {text.slice(at + q.length)}
    </>
  );
}

function Initials({ name, active }: { name: string; active: boolean }) {
  const [last = '', first = ''] = name.split(',').map((part) => part.trim());
  const letters = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
  return (
    <span
      aria-hidden="true"
      className={`flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold tracking-wide
                  ${active ? 'bg-white/15 text-white' : 'bg-neutral-100 text-neutral-600'}`}
    >
      {letters}
    </span>
  );
}

function PlaceIcon({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-9 shrink-0 items-center justify-center rounded-full
                  ${active ? 'bg-white/15 text-white' : 'bg-neutral-100 text-neutral-600'}`}
    >
      <svg viewBox="0 0 24 24" className="size-4 fill-none stroke-current stroke-2">
        <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" strokeLinejoin="round" />
        <circle cx="12" cy="9.5" r="2.5" />
      </svg>
    </span>
  );
}

function Kbd({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return (
    <kbd
      className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-md border px-1.5 py-0.5 font-sans
                  text-[0.6875rem] font-semibold
                  ${dark ? 'border-white/30 text-white' : 'border-neutral-300 bg-white text-neutral-500'}`}
    >
      {children}
    </kbd>
  );
}
